import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import { motion, AnimatePresence } from "framer-motion";
import { faSignOutAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "@cakrawalajakarta/shared-components";

export const Navbar = () => {
  const [user, setUser] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState("");
  const auth = getAuth();
  const menuRef = useRef(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, [auth]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Error saat logout:", error.message);
    }
  };

  const handleHamburgerClick = () => {
    setMenuOpen(!menuOpen);
  };

  const handleMenuItemClick = (action) => {
    if (action === "toggleProfile") {
      setDropdownOpen(!dropdownOpen);
    } else if (action === "navigate") {
      setMenuOpen(false);
      setDropdownOpen(false);
    }
  };

  return (
    <nav className="w-full sticky-navbar mt-6 py-4 bg-white shadow-lg text-lime-800">
      <div
        className="container mx-auto px-4 flex items-center justify-between"
        ref={menuRef}
      >
        {/* 🔹 Logo & Teks di Samping (Hanya Desktop) */}
        <div className="flex-1 md:flex md:items-center">
          <Link
            to="/"
            className="flex items-center space-x-4 text-2xl font-bold mx-auto md:mx-0"
          >
            <img
              src="/favIcon.svg"
              alt="Logo"
              className="w-14 h-auto md:w-14 lg:w-14"
              onError={(e) => {
                console.error("Gagal memuat ikon:", e);
                e.target.src = "https://via.placeholder.com/56"; // Fallback image
              }}
            />
            <span className="hidden md:block text-2xl font-black text-lime-600">
              JOTR GEPANG
            </span>
          </Link>
        </div>

        {/* 🔹 Teks "JOTR GEPANG" di Tengah (Mobile) */}
        <span className="block md:hidden absolute left-1/2 transform -translate-x-1/2 text-base font-semibold oneline whitespace-nowrap">
          JOTR GEPANG
        </span>

        {/* 🔹 Menu (Desktop) */}
        <div className="hidden md:flex items-center space-x-8 md:space-x-4 text-base uppercase font-medium px-8 md:px-04">
          <Link
            to="/schedule"
            className={`hover:text-lime-600 ${
              activeMenu === "schedule" ? "border-b-2 border-lime-600" : ""
            }`}
            onClick={() => setActiveMenu("schedule")}
          >
            SCHEDULE
          </Link>

          <Link
            to="/RaceRegulations"
            className={`hover:text-lime-600 ${
              activeMenu === "regulations" ? "border-b-2 border-lime-600" : ""
            }`}
            onClick={() => setActiveMenu("regulations")}
          >
            REGULATIONS
          </Link>

          <Link
            to="/entrylistpage"
            className={`hover:text-lime-600 ${
              activeMenu === "entrylist" ? "border-b-2 border-lime-600" : ""
            }`}
            onClick={() => setActiveMenu("entrylist")}
          >
            ENTRY LIST
          </Link>

          <a
            href="https://results.teratas.id/20189/jotr2025"
            target="_blank"
            rel="noopener noreferrer"
            className={`hover:text-lime-600 ${
              activeMenu === "results" ? "border-b-2 border-lime-600" : ""
            }`}
            onClick={() => setActiveMenu("results")}
          >
            RESULTS
          </a>

          <Link
            to="/gallery"
            className={`hover:text-lime-600 ${
              activeMenu === "gallery" ? "border-b-2 border-lime-600" : ""
            }`}
            onClick={() => setActiveMenu("gallery")}
          >
            GALLERY
          </Link>

          <Link
            to="/Kontak"
            className={`hover:text-lime-600 ${
              activeMenu === "contact" ? "border-b-2 border-lime-600" : ""
            }`}
            onClick={() => setActiveMenu("contact")}
          >
            KONTAK
          </Link>
        </div>

        {/* 🔹 Login / Avatar */}
        <div className="hidden md:flex items-center space-x-4">
          {user ? (
            <div
              className="relative flex items-center space-x-4"
              onMouseEnter={() => setDropdownOpen(true)}
              onMouseLeave={() => setDropdownOpen(false)}
            >
              <div className="w-10 h-10 rounded-full overflow-hidden">
                <img
                  src={user.photoURL || "https://ui-avatars.com/api/?name=User"}
                  alt="Avatar"
                  className="w-full h-full object-cover"
                />
              </div>

              <AnimatePresence>
                {dropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute right-0 top-full mt-8 w-52 bg-white shadow-lg rounded-lg overflow-hidden"
                  >
                    <Link
                      to="/Profile"
                      className="block px-4 py-2 text-lime-800 hover:bg-slate-100 hover:text-lime-600"
                    >
                      My Profile
                    </Link>
                    <Link
                      to="/MyRegistration"
                      className="block px-4 py-2 text-lime-800 hover:bg-slate-100 hover:text-lime-600"
                    >
                      My Registration
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-red-600 hover:bg-slate-100 hover:text-red-600"
                    >
                      Logout
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ) : (
            <Button
              to="/Login"
              variant="none"
              className="ml-6 h-auto py-2 px-6 bg-lime-600 text-white hover:bg-lime-800"
            >
              Login
            </Button>
          )}
        </div>

        {/* 🔹 Tombol Menu Mobile */}
        <button
          className="md:hidden focus:outline-none"
          onClick={handleHamburgerClick}
        >
          <svg
            className="w-8 h-8"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            {menuOpen ? (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            ) : (
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16m-7 6h7"
              />
            )}
          </svg>
        </button>
      </div>

      {/* 🔹 Dropdown Menu untuk Mobile */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute top-full left-0 w-full bg-white shadow-lg md:hidden min-h-screen"
          >
            <div className="flex flex-col text-center w-full px-4 uppercase font-medium divide-y divide-slate-200 max-w-lg mx-auto">
              <div className="flex flex-col py-4 space-y-2">
                <Link
                  to="/schedule"
                  className="py-2 hover:text-lime-600"
                  onClick={() => {
                    setActiveMenu("schedule");
                    handleMenuItemClick("navigate");
                  }}
                >
                  SCHEDULE
                </Link>

                <Link
                  to="/RaceRegulations"
                  className="py-2 hover:text-lime-600"
                  onClick={() => {
                    setActiveMenu("regulations");
                    handleMenuItemClick("navigate");
                  }}
                >
                  REGULATIONS
                </Link>

                <Link
                  to="/entrylistpage"
                  className="py-2 hover:text-lime-600"
                  onClick={() => {
                    setActiveMenu("entrylist");
                    handleMenuItemClick("navigate");
                  }}
                >
                  ENTRY LIST
                </Link>

                <a
                  href="https://results.teratas.id/20189/jotr2025"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="py-2 hover:text-lime-600"
                  onClick={() => {
                    setActiveMenu("results");
                    handleMenuItemClick("navigate");
                  }}
                >
                  RESULTS
                </a>

                <Link
                  to="/gallery"
                  className="py-2 hover:text-lime-600"
                  onClick={() => {
                    setActiveMenu("gallery");
                    handleMenuItemClick("navigate");
                  }}
                >
                  GALLERY
                </Link>

                <Link
                  to="/Kontak"
                  className="py-2 hover:text-lime-600"
                  onClick={() => {
                    setActiveMenu("contact");
                    handleMenuItemClick("navigate");
                  }}
                >
                  KONTAK
                </Link>
              </div>

              {/* Profile Related Links */}
              {user ? (
                <div className="flex flex-col py-4 space-y-2">
                  <Link
                    to="/Profile"
                    className="py-2 text-lime-800 hover:text-lime-600"
                    onClick={() => handleMenuItemClick("navigate")}
                  >
                    MY PROFILE
                  </Link>
                  <Link
                    to="/MyRegistration"
                    className="py-2 text-lime-800 hover:text-lime-600"
                    onClick={() => handleMenuItemClick("navigate")}
                  >
                    MY REGISTRATION
                  </Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      handleMenuItemClick("navigate");
                    }}
                    className="py-2 text-red-600 hover:text-red-700 w-full flex items-center justify-center gap-2"
                  >
                    <span>LOGOUT</span>
                    <FontAwesomeIcon icon={faSignOutAlt} />
                  </button>
                </div>
              ) : (
                <Link
                  to="/Login"
                  className="py-2 text-lime-800 hover:text-lime-600"
                  onClick={() => handleMenuItemClick("navigate")}
                >
                  LOGIN
                </Link>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};
